import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import SectionHeader from '@/components/ui/SectionHeader'
import ArtifactCard from '@/components/ui/ArtifactCard'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { useSEO } from '@/hooks/useSEO'
import type { Theme, PodcastEpisode, YouTubeVideo, Artifact } from '@/types'
import * as api from '@/lib/api'

export default function ThemeDetailPage() {
  const { slug } = useParams<{ slug: string }>()
  const [loading, setLoading] = useState(true)
  const [theme, setTheme] = useState<Theme | null>(null)
  const [podcasts, setPodcasts] = useState<PodcastEpisode[]>([])
  const [videos, setVideos] = useState<YouTubeVideo[]>([])
  const [artifacts, setArtifacts] = useState<Artifact[]>([])

  // SEO
  useSEO({
    title: theme?.title,
    description: theme?.description,
    image: theme?.hero_image_url,
    url: theme ? `https://thearchivenetwork.com/themes/${theme.slug}` : undefined,
  })

  useEffect(() => {
    async function loadData() {
      if (!slug) return
      try {
        const data = await api.getThemeContent(slug)
        setTheme(data.theme)
        setPodcasts(data.podcasts)
        setVideos(data.videos)
        setArtifacts(data.artifacts)
      } catch (error) {
        console.error('Failed to load theme:', error)
      } finally {
        setLoading(false)
      }
    }
    loadData()
  }, [slug])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!theme) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-display text-2xl text-archive-cream">Theme Not Found</h1>
          <Link to="/themes" className="mt-4 btn-secondary inline-block">
            Browse Themes
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="animate-fade-in">
      {/* Hero */}
      <section className="relative py-24 sm:py-32">
        {/* Background image */}
        {theme.hero_image_url && (
          <div className="absolute inset-0 z-0">
            <img
              src={theme.hero_image_url}
              alt=""
              className="w-full h-full object-cover opacity-60"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-archive-black/40 via-archive-black/60 to-archive-black" />
          </div>
        )}

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* Breadcrumb */}
          <nav className="mb-8">
            <ol className="flex items-center justify-center gap-2 text-sm text-archive-muted">
              <li><Link to="/themes" className="hover:text-archive-gold">Themes</Link></li>
              <li>/</li>
              <li className="text-archive-text">{theme.title}</li>
            </ol>
          </nav>

          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-archive-cream">
            {theme.title}
          </h1>
          <p className="mt-6 text-lg text-archive-muted max-w-2xl mx-auto">
            {theme.description}
          </p>
        </div>
      </section>

      {/* Long Description */}
      {theme.long_description && (
        <section className="py-12 sm:py-16">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="prose prose-lg prose-invert max-w-none">
              {theme.long_description.split('\n\n').map((paragraph, index) => (
                <p key={index} className="text-archive-text leading-relaxed mb-6 last:mb-0">
                  {paragraph}
                </p>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Related Podcasts */}
      {podcasts.length > 0 && (
        <section className="py-16 bg-archive-charcoal/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeader
              title="Related Podcasts"
              subtitle={`Episodes exploring ${theme.title}`}
              ctaText="All podcasts"
              ctaLink="/podcasts"
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {podcasts.map((episode) => (
                <ArtifactCard
                  key={episode.id}
                  title={episode.title}
                  description={episode.summary}
                  imageUrl={
                    episode.cover_image_url ||
                    episode.series_cover_image_url ||
                    `/images/podcasts/${episode.archive_key}.jpg`
                  }
                  linkTo={`/podcast/${episode.id}`}
                  type="podcast"
                  archiveKey={episode.archive_key}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Related Videos */}
      {videos.length > 0 && (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeader
              title="Related Conversations"
              subtitle={`Videos about ${theme.title}`}
              ctaText="All conversations"
              ctaLink="/conversations"
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {videos.map((video) => (
                <ArtifactCard
                  key={video.id}
                  title={video.title}
                  description={video.summary}
                  imageUrl={video.thumbnail_url}
                  linkTo={`/video/${video.id}`}
                  type="video"
                  archiveKey={video.archive_key}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Related Artifacts */}
      {artifacts.length > 0 && (
        <section className="py-16 bg-archive-charcoal/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeader
              title="Related Artifacts"
              subtitle={`Collectibles from ${theme.title}`}
              ctaText="All artifacts"
              ctaLink="/artifacts"
            />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {artifacts.map((artifact) => (
                <ArtifactCard
                  key={artifact.slug}
                  title={artifact.title}
                  subtitle={artifact.subtitle}
                  imageUrl={artifact.image_url}
                  linkTo={`/artifact/${artifact.slug}`}
                  archiveKey={artifact.archive_key}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* No content fallback */}
      {podcasts.length === 0 && videos.length === 0 && artifacts.length === 0 && (
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <p className="text-archive-muted">
              Content for this theme is being curated. Check back soon.
            </p>
          </div>
        </section>
      )}
    </div>
  )
}
